import React, { useState, useEffect, useMemo } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  LinearProgress, 
  useTheme,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Breadcrumbs,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import { 
  ChevronRight, 
  ChevronDown,
  Clock,
  CheckCircle,
  AlertTriangle,
  XCircle,
  ArrowRight,
  FolderOpen,
  MoveRight,
} from 'lucide-react';
import { Case, CaseStage, Task } from '../../types';
import { caseStages } from '../../data/caseStages';

interface CaseProgressProps {
  case_: Case;
  currentStage: CaseStage;
  currentStages: CaseStage[];
  currentStageIndex: number;
  currentCategoryIndex: number;
  categories: string[];
  onStatusChange?: (updates: { status: Case['status']; currentStageId: string }) => void;
  onStageView?: (stageId: string) => void;
}

interface ProgressChangeConfirmation {
  open: boolean;
  status: Case['status'];
  stageId: string;
  isAutoProgress?: boolean;
}

export const CaseProgress: React.FC<CaseProgressProps> = ({
  case_,
  currentStage,
  currentStages,
  currentStageIndex,
  currentCategoryIndex,
  categories,
  onStatusChange,
  onStageView,
}) => {
  const theme = useTheme();
  const [statusAnchorEl, setStatusAnchorEl] = useState<null | HTMLElement>(null);
  const [categoryAnchorEl, setCategoryAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [confirmProgress, setConfirmProgress] = useState<ProgressChangeConfirmation>({
    open: false,
    status: case_.status,
    stageId: currentStage.id,
    isAutoProgress: false,
  });

  // Calculate next stage
  const nextStage = useMemo(() => {
    const allStages = caseStages;
    const currentIndex = allStages.findIndex(s => s.id === currentStage.id);
    return currentIndex < allStages.length - 1 ? allStages[currentIndex + 1] : null;
  }, [currentStage]);

  // Check if all tasks are complete
  const areAllTasksComplete = useMemo(() => {
    const currentStageTasks = case_.tasks.filter(task => task.stageId === currentStage.id);
    return currentStageTasks.length > 0 && currentStageTasks.every(task => task.status === 'completed');
  }, [case_.tasks, currentStage.id]);

  // Effect to check task completion and prompt for progression
  useEffect(() => {
    if (areAllTasksComplete && nextStage) {
      setConfirmProgress({
        open: true,
        status: case_.status,
        stageId: nextStage.id,
        isAutoProgress: true,
      });
    }
  }, [areAllTasksComplete, nextStage, case_.status]);

  // Calculate overall progress
  const totalStages = caseStages.length;
  const currentStageOrder = caseStages.findIndex(stage => stage.id === currentStage.id);
  const overallProgress = ((currentStageOrder + 1) / totalStages) * 100;

  // Calculate category progress
  const categoryProgress = ((currentStageIndex + 1) / currentStages.length) * 100;

  const handleStatusClick = (event: React.MouseEvent<HTMLElement>) => {
    setStatusAnchorEl(event.currentTarget);
  };

  const handleStatusClose = () => {
    setStatusAnchorEl(null);
  };

  const handleCategoryClick = (event: React.MouseEvent<HTMLElement>) => {
    setCategoryAnchorEl(event.currentTarget);
    setSelectedCategory(null);
  };

  const handleCategoryClose = () => {
    setCategoryAnchorEl(null);
    setSelectedCategory(null);
  };

  const handleCategorySelect = (category: string) => {
    setSelectedCategory(category);
  };

  const handleStageView = (stageId: string) => {
    onStageView?.(stageId);
    handleCategoryClose();
  };

  const handleProgressChange = (status: Case['status'], stageId: string) => {
    setConfirmProgress({
      open: true,
      status,
      stageId,
      isAutoProgress: false,
    });
    handleStatusClose();
  };

  const handleConfirmProgressChange = () => {
    onStatusChange?.({
      status: confirmProgress.status,
      currentStageId: confirmProgress.stageId,
    });
    setConfirmProgress(prev => ({ ...prev, open: false }));
  };

  const handleCancelProgressChange = () => {
    setConfirmProgress(prev => ({ ...prev, open: false }));
  };

  const getStatusIcon = (status: Case['status']) => {
    switch (status) {
      case 'active':
        return <Clock size={18} className="text-green-500" />;
      case 'pending':
        return <AlertTriangle size={18} className="text-amber-500" />;
      case 'closed':
        return <CheckCircle size={18} className="text-blue-500" />;
      default:
        return <Clock size={18} className="text-gray-500" />;
    }
  };

  const getStatusColor = (status: Case['status']) => {
    switch (status) {
      case 'active':
        return theme.palette.success.main;
      case 'pending':
        return theme.palette.warning.main;
      case 'closed':
        return theme.palette.info.main;
      default:
        return theme.palette.text.secondary;
    }
  };

  const getNextStageText = () => {
    const targetStage = caseStages.find(s => s.id === confirmProgress.stageId);
    if (!targetStage) return '';
    
    if (confirmProgress.isAutoProgress) {
      return `advance to "${targetStage.name}" (all tasks completed)`;
    }
    
    const currentOrder = caseStages.findIndex(s => s.id === currentStage.id);
    const targetOrder = caseStages.findIndex(s => s.id === targetStage.id);
    
    if (targetOrder > currentOrder) {
      return `advance to "${targetStage.name}"`;
    } else if (targetOrder < currentOrder) {
      return `move back to "${targetStage.name}"`;
    } else {
      return `update status to ${confirmProgress.status}`;
    }
  };

  return (
    <Paper 
      sx={{ 
        p: 2.5,
        mb: 4,
        background: theme.palette.mode === 'light' 
          ? 'linear-gradient(to right, rgba(30, 58, 95, 0.03), rgba(45, 78, 125, 0.05))'
          : 'linear-gradient(to right, rgba(19, 40, 68, 0.2), rgba(30, 58, 95, 0.3))',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <Breadcrumbs
              separator={<ChevronRight size={16} />}
              sx={{ 
                '& .MuiBreadcrumbs-separator': {
                  mx: 0.5,
                },
              }}
            >
              <Link
                component="button"
                variant="body2"
                onClick={handleCategoryClick}
                sx={{ 
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  color: 'text.secondary',
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                <FolderOpen size={16} />
                {categories[currentCategoryIndex]}
              </Link>
              <Typography 
                variant="body2"
                sx={{ 
                  color: 'text.primary',
                  fontWeight: 500,
                }}
              >
                {currentStage.name}
              </Typography>
            </Breadcrumbs>
          </Box>
          <Typography 
            variant="body2" 
            color="text.secondary"
            sx={{ 
              fontSize: '0.8125rem',
              maxWidth: '80%',
            }}
          >
            {currentStage.description}
          </Typography>
        </Box>
        <Box 
          sx={{ 
            textAlign: 'right',
            minWidth: 100,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mb: 1 }}>
            <IconButton
              size="small"
              onClick={handleStatusClick}
              sx={{ 
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                px: 1,
                py: 0.5,
                '& .MuiSvgIcon-root': {
                  fontSize: '1.25rem',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {getStatusIcon(case_.status)}
                <Typography 
                  variant="body2"
                  sx={{ 
                    textTransform: 'capitalize',
                    color: getStatusColor(case_.status),
                    fontWeight: 500,
                  }}
                >
                  {case_.status}
                </Typography>
                <ChevronDown size={16} />
              </Box>
            </IconButton>
          </Box>
          <Typography 
            variant="caption" 
            color="text.secondary"
            sx={{ fontSize: '0.75rem' }}
          >
            Overall Progress: {Math.round(overallProgress)}%
          </Typography>
        </Box>
      </Box>

      <Box sx={{ mt: 2 }}>
        <Box sx={{ mb: 1.5 }}>
          <LinearProgress 
            variant="determinate" 
            value={categoryProgress}
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: theme.palette.mode === 'light'
                ? 'rgba(30, 58, 95, 0.08)'
                : 'rgba(30, 58, 95, 0.2)',
              '& .MuiLinearProgress-bar': {
                borderRadius: 4,
                background: theme.palette.mode === 'light'
                  ? 'linear-gradient(to right, #1e3a5f, #2d4e7d)'
                  : 'linear-gradient(to right, #2d4e7d, #3d6ea7)',
              },
            }}
          />
        </Box>

        <Box 
          sx={{ 
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography 
            variant="caption" 
            color="text.secondary"
            sx={{ fontSize: '0.75rem' }}
          >
            {Math.round(categoryProgress)}% through {categories[currentCategoryIndex]}
          </Typography>
          {nextStage && (
            <Typography 
              variant="caption"
              sx={{ 
                fontSize: '0.75rem',
                color: theme.palette.text.secondary,
              }}
            >
              Next: {nextStage.name}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Status Menu - For changing case progress */}
      <Menu
        anchorEl={statusAnchorEl}
        open={Boolean(statusAnchorEl)}
        onClose={handleStatusClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 220,
          }
        }}
      >
        <Typography
          variant="caption"
          sx={{
            px: 2,
            py: 1,
            display: 'block',
            color: 'text.secondary',
          }}
        >
          Case Status
        </Typography>
        <MenuItem onClick={() => handleProgressChange('active', currentStage.id)}>
          <ListItemIcon>
            <Clock size={18} className="text-green-500" />
          </ListItemIcon>
          <ListItemText>Active</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleProgressChange('pending', currentStage.id)}>
          <ListItemIcon>
            <AlertTriangle size={18} className="text-amber-500" />
          </ListItemIcon>
          <ListItemText>Pending</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleProgressChange('closed', currentStage.id)}>
          <ListItemIcon>
            <CheckCircle size={18} className="text-blue-500" />
          </ListItemIcon>
          <ListItemText>Closed</ListItemText>
        </MenuItem>

        <Divider sx={{ my: 1 }} />

        <Typography
          variant="caption"
          sx={{
            px: 2,
            py: 1,
            display: 'block',
            color: 'text.secondary',
          }}
        >
          Move Case Progress
        </Typography>
        {currentStages.map((stage) => (
          <MenuItem 
            key={stage.id}
            onClick={() => handleProgressChange(case_.status, stage.id)}
            selected={stage.id === currentStage.id}
          >
            <ListItemIcon>
              {stage.id === currentStage.id ? (
                <ArrowRight size={18} />
              ) : (
                <Box sx={{ width: 18 }} />
              )}
            </ListItemIcon>
            <ListItemText primary={stage.name} />
          </MenuItem>
        ))}
      </Menu>

      {/* Navigation Menu - For viewing different stages */}
      <Menu
        anchorEl={categoryAnchorEl}
        open={Boolean(categoryAnchorEl)}
        onClose={handleCategoryClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 280,
            maxHeight: '70vh',
          }
        }}
      >
        {selectedCategory === null ? (
          <>
            <Typography
              variant="caption"
              sx={{
                px: 2,
                py: 1,
                display: 'block',
                color: 'text.secondary',
              }}
            >
              Navigate Case Stages
            </Typography>
            {categories.map((category) => (
              <MenuItem 
                key={category}
                onClick={() => handleCategorySelect(category)}
                selected={category === categories[currentCategoryIndex]}
              >
                <ListItemIcon>
                  <FolderOpen size={18} />
                </ListItemIcon>
                <ListItemText 
                  primary={category}
                  secondary={`${caseStages.filter(s => s.category === category).length} stages`}
                />
                <ChevronRight size={16} />
              </MenuItem>
            ))}
          </>
        ) : (
          <>
            <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton size="small" onClick={() => setSelectedCategory(null)}>
                <ChevronRight size={16} style={{ transform: 'rotate(180deg)' }} />
              </IconButton>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                }}
              >
                {selectedCategory} Stages
              </Typography>
            </Box>
            <Divider />
            {caseStages
              .filter(stage => stage.category === selectedCategory)
              .map((stage) => (
                <MenuItem 
                  key={stage.id}
                  onClick={() => handleStageView(stage.id)}
                  selected={stage.id === currentStage.id}
                >
                  <ListItemIcon>
                    {stage.id === currentStage.id ? (
                      <ArrowRight size={18} />
                    ) : (
                      <Box sx={{ width: 18 }} />
                    )}
                  </ListItemIcon>
                  <ListItemText 
                    primary={stage.name}
                    secondary={stage.description}
                    secondaryTypographyProps={{
                      sx: {
                        fontSize: '0.75rem',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }
                    }}
                  />
                </MenuItem>
              ))}
          </>
        )}
      </Menu>

      {/* Confirmation Dialog for Progress Changes */}
      <Dialog
        open={confirmProgress.open}
        onClose={handleCancelProgressChange}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle>
          {confirmProgress.isAutoProgress ? 'Stage Complete' : 'Confirm Case Progress Change'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography>
              Are you sure you want to {getNextStageText()}?
            </Typography>
            {confirmProgress.isAutoProgress ? (
              <Typography variant="body2" color="text.secondary">
                All tasks in the current stage have been completed. The case is ready to move to the next stage.
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                This action will update the case progress and may affect related tasks and timelines.
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCancelProgressChange} color="inherit">
            {confirmProgress.isAutoProgress ? 'Stay Here' : 'Cancel'}
          </Button>
          <Button
            onClick={handleConfirmProgressChange}
            variant="contained"
            startIcon={<MoveRight size={18} />}
          >
            {confirmProgress.isAutoProgress ? 'Proceed to Next Stage' : 'Confirm Change'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};